import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useActiveCompanyId } from "../../api/useActiveCompanyId";
import { errorMessage } from "../../api/errorMessage";
import { BusinessPage } from "./BusinessPage";
import { loadTickets, oldClosedTicketCount, ticketStatusClass, visibleTickets, Ticket } from "./ticketList";
import "../SupportTicketsPage/supportTickets.scss";
import "./ticketsPage.scss";
export function TicketsPage({ design = false }: { design?: boolean }) {
    const { value: companyId, resolved } = useActiveCompanyId();
    const [tickets, setTickets] = useState<Ticket[]>([]); const [loading, setLoading] = useState(true); const [error, setError] = useState(""); const [retry, setRetry] = useState(0);
    const [search, setSearch] = useState(""); const [status, setStatus] = useState("all"); const [archive, setArchive] = useState(false);
    const base = design ? "/order-layout" : "/support";
    const path = design ? (process.env.REACT_APP_DESIGN_GATEWAY_PATH || "/api/design/gateway/tasks") : (process.env.REACT_APP_LKFR_GATEWAY_PATH || "/api/gateway/tasks");
    useEffect(() => {
        const abort = new AbortController(); setTickets([]); setError(""); setArchive(false);
        if (!resolved || !companyId) { setLoading(!resolved); return () => abort.abort(); }
        setLoading(true);
        loadTickets(path, companyId, abort.signal).then(data => { if (!abort.signal.aborted) setTickets(data); }).catch(e => { if (!abort.signal.aborted) setError(errorMessage(e)); }).finally(() => { if (!abort.signal.aborted) setLoading(false); });
        return () => abort.abort();
    }, [companyId, resolved, path, retry]);
    const items = useMemo(() => visibleTickets(tickets, archive).filter(ticket => (status === "all" || status === ticket.status) && `${ticket.id} ${ticket.title} ${ticket.objectLabel}`.toLocaleLowerCase("ru-RU").includes(search.trim().toLocaleLowerCase("ru-RU"))).sort((a,b)=>Number(a.status==="closed")-Number(b.status==="closed") || b.lastActivity.localeCompare(a.lastActivity) || b.id-a.id), [tickets,archive,status,search]);
    return (
        <div className="lkfr-tickets-page">
            <BusinessPage title={design ? "Заказы дизайна" : "Поддержка"} subtitle={design ? "Макеты, согласования и переписка с дизайнерами." : "Оборудование, кассы и работа салона."}>
                <div className="tickets-actions">
                    <Link className="business-primary" to={base + "/new"}>
                        {design ? "+ Заказать дизайн" : "+ Новое обращение"}
                    </Link>
                    {!design && <Link className="business-secondary" to="/knowledge/8">Как создать обращение</Link>}
                    <button
                        type="button"
                        className="business-secondary tickets-refresh"
                        aria-label="Обновить"
                        title="Обновить обращения"
                        disabled={loading}
                        onClick={() => setRetry(value => value + 1)}
                    >
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true" focusable="false">
                            <path d="M20 7v5h-5M20 12a8 8 0 1 0-2.34 5.66" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>
                <label className="tickets-search">
                    <span className="tickets-search-label">Поиск обращений</span>
                    <input className="section-input" type="search" value={search} placeholder="Номер, тема или адрес" onChange={event => setSearch(event.target.value)} />
                </label>
                <div className="lkfr-tabs tickets-filters" aria-label="Статус обращений">
                    {[["all", "Все", "Все"], ["open", "Открытые", "Открытые"], ["waiting", "Ожидают", "Ожидают ответа"], ["closed", "Закрытые", "Закрытые"]].map(([value, label, accessibleLabel]) => (
                        <button type="button" key={value} aria-label={accessibleLabel} aria-pressed={value === status} onClick={() => setStatus(value)}>{label}</button>
                    ))}
                </div>
                {oldClosedTicketCount(tickets) > 0 && (
                    <label className="tickets-archive">
                        <input type="checkbox" checked={archive} onChange={event => setArchive(event.target.checked)} />
                        <span>Закрытые старше 14 дней ({oldClosedTicketCount(tickets)})</span>
                    </label>
                )}
                {loading && <p role="status">Загружаем обращения…</p>}
                {error && <p className="business-error" role="alert">{error} <button type="button" onClick={() => setRetry(value => value + 1)}>Повторить</button></p>}
                {!companyId && resolved && <p>Выберите салон в верхней части страницы.</p>}
                {!loading && companyId && !error && !items.length && (
                    <section className="business-card tickets-empty">
                        <h2>{tickets.length ? "По выбранным фильтрам обращений нет" : "Обращений пока нет"}</h2>
                        <p>Создайте обращение или измените поиск и статус.</p>
                    </section>
                )}
                <div className="support-tickets-list">
                    {items.map(ticket => (
                        <Link to={`${base}/ticket/${ticket.id}`} className={`support-ticket-card status-${ticketStatusClass(ticket)}`} key={ticket.id}>
                            <div className="ticket-card-header">
                                <span className="ticket-number">#{ticket.id}</span>
                                <span className="ticket-status">{ticket.statusLabel}</span>
                            </div>
                            <h2 className="ticket-title" title={ticket.title}>{ticket.title}</h2>
                            {ticket.terminalPaymentBlocked && <div className="ticket-urgent-flag">Терминал не принимает оплаты</div>}
                            {ticket.objectLabel && <p className="ticket-object" title={ticket.objectLabel}>{ticket.objectLabel}</p>}
                            <div className="ticket-card-dates">
                                <span>Создано: {ticket.createdAt || "—"}</span>
                                <span>Активность: {ticket.lastActivity || "—"}</span>
                            </div>
                        </Link>
                    ))}
                </div>
            </BusinessPage>
        </div>
    );
}
